<template>
    <v-dialog v-model="open" max-width="1000" persistent scrollable>
        <v-card height="90vh" :loading="loading">
            <v-overlay :value="loading" absolute>
                <v-progress-circular indeterminate color="primary" size="64" />
            </v-overlay>
            
            <v-card-title class="d-flex justify-space-between align-center">
                {{ titleProp }}
                <v-btn icon @click="$emit('close', jobcard)">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            
            <v-tabs v-if="screenState === 'form'" v-model="activeTab" background-color="primary" style="flex: none">
                <v-tab>Jobcard</v-tab>
                <v-tab>Inspection</v-tab>
                <v-tab>Checks</v-tab>
                <v-tab>Tyres</v-tab>
                <v-tab>Battery</v-tab>
                <v-tab>Uploads</v-tab>
                <v-tab>Costing</v-tab>
                <v-tab>Approvals</v-tab>
            </v-tabs>

            <v-card-text class="pt-6">
                <!-- Loading -->
                <div v-if="screenState === 'loading'" class="d-flex align-center justify-center" style="min-height: 300px">
                    <v-progress-circular indeterminate color="primary" size="48" />
                </div>

                <!-- Load error -->
                <div v-else-if="screenState === 'error'" class="d-flex flex-column align-center justify-center text-center" style="min-height: 300px">
                    <v-icon color="error" size="48" class="mb-3">mdi-alert-circle-outline</v-icon>
                    <div class="body-1 mb-4">Failed to load jobcard details. Please check your connection and try again.</div>
                    <v-btn color="primary" @click="loadInitialData">Retry</v-btn>
                </div>

                <!-- Select existing or new -->
                <template v-else-if="screenState === 'select'">
                    <div class="text-h6 mb-2">Existing vehicle jobcards</div>
                    <div class="body-2 grey--text text--darken-1 mb-4">Select an existing jobcard, or start a new one.</div>

                    <v-list two-line class="pa-0">
                        <template v-for="(jobcard, index) in existingJobcards">
                            <v-list-item
                                :key="index"
                                ripple
                                @click="selectJobcard(jobcard)"
                            >
                                <v-list-item-content>
                                    <v-list-item-title class="font-weight-medium text-left">
                                        {{ quoteRequestJobcardType[jobcard.jobcardDetails.type] || 'Jobcard' }}
                                        <v-chip v-if="jobcard.quoteRequest" small color="primary" class="ml-2">{{ jobcard.quoteRequest.reference }}</v-chip>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        <v-row no-gutters class="mt-1 text-left">
                                            <v-col v-if="jobcard.jobcardDetails.ctaRef" cols="12" sm="4" class="caption pr-4">
                                                <span class="grey--text text--darken-1">CTA Ref: </span><b>{{ jobcard.jobcardDetails.ctaRef }}</b>
                                            </v-col>
                                            <v-col v-if="jobcard.jobcardDetails.requestedBy" cols="12" sm="4" class="caption pr-4">
                                                <span class="grey--text text--darken-1">Requested By: </span><b>{{ jobcard.jobcardDetails.requestedBy }}</b>
                                            </v-col>
                                            <v-col v-if="jobcard.jobcardDetails.dateRequested" cols="12" sm="4" class="caption pr-4">
                                                <span class="grey--text text--darken-1">Requested Date: </span><b>{{ formattedDate(jobcard.jobcardDetails.dateRequested) }}</b>
                                            </v-col>
                                        </v-row>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider :key="`d-${index}`" />
                        </template>
                    </v-list>

                    <div class="mt-4">
                        <v-btn outlined color="primary" @click="startNewJobcard">
                            <v-icon left>mdi-plus</v-icon>New Jobcard
                        </v-btn>
                    </div>
                </template>

                <!-- Form tabs -->
                <v-form v-else ref="jobcardForm">
                    <v-tabs-items ref="tabsItems" v-model="activeTab">
                        <!-- Jobcard Details -->
                        <v-tab-item eager>
                            <div class="text-h6 mb-4">Jobcard Details</div>
                            <v-row class="pt-3">
                                <v-col cols="12" sm="6">
                                    <v-select
                                        v-model="jobcard.jobcardDetails.type"
                                        :items="jobcardTypes"
                                        :rules="required"
                                        label="Jobcard Type"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.ctaRef"
                                        :rules="required"
                                        label="CTA Ref"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.requestedBy"
                                        :rules="required"
                                        label="Requested By"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.fleetController"
                                        :rules="required"
                                        label="Fleet Controller"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.branch"
                                        :rules="required"
                                        label="Branch"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.driver"
                                        :rules="required"
                                        label="Driver"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <v-dialog v-model="dateRequestedPickerOpen" width="290px">
                                        <template #activator="{ on, attrs }">
                                            <v-text-field
                                                :value="formattedDate(jobcard.jobcardDetails.dateRequested)"
                                                :rules="required"
                                                label="Date Requested"
                                                prepend-inner-icon="mdi-calendar"
                                                readonly
                                                outlined
                                                dense
                                                hide-details
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            v-model="jobcard.jobcardDetails.dateRequested"
                                            no-title
                                            full-width
                                            @input="dateRequestedPickerOpen = false"
                                        >
                                            <v-spacer />
                                            <v-btn text color="primary" @click="dateRequestedPickerOpen = false">Cancel</v-btn>
                                        </v-date-picker>
                                    </v-dialog>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.registrationNumber"
                                        :rules="required"
                                        label="Registration Number"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>

                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        v-model.number="jobcard.jobcardDetails.kilometres"
                                        :rules="required"
                                        label="Kilometres"
                                        type="number"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.make"
                                        :rules="required"
                                        label="Make"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.model"
                                        :rules="required"
                                        label="Model"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                            
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.lastService"
                                        label="Last Service"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field
                                        v-model="jobcard.jobcardDetails.lastServiceDate"
                                        label="Last Service Date"
                                        prepend-inner-icon="mdi-calendar"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>

                                <v-col cols="12" sm="4">
                                    <v-radio-group
                                        v-model="jobcard.jobcardDetails.gpsTested"
                                        :rules="required"
                                        label="GPS Tested"
                                        row
                                        hide-details
                                        class="mt-0"
                                    >
                                        <v-radio label="Yes" :value="true" />
                                        <v-radio label="No" :value="false" />
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-radio-group
                                        v-model="jobcard.jobcardDetails.didTagTested"
                                        :rules="required"
                                        label="DID / Tag Tested"
                                        row
                                        hide-details
                                        class="mt-0"
                                    >
                                        <v-radio label="Yes" :value="true" />
                                        <v-radio label="No" :value="false" />
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-radio-group
                                        v-model="jobcard.jobcardDetails.fuelTested"
                                        :rules="required"
                                        label="Fuel Tested"
                                        row
                                        hide-details
                                        class="mt-0"
                                    >
                                        <v-radio label="Yes" :value="true" />
                                        <v-radio label="No" :value="false" />
                                    </v-radio-group>
                                </v-col>

                                <v-col cols="12">
                                    <v-textarea
                                        v-model="jobcard.jobcardDetails.reason"
                                        :rules="required"
                                        label="Reason"
                                        outlined
                                        dense
                                        rows="3"
                                        no-resize
                                        hide-details
                                    />
                                </v-col>
                            </v-row>
                        </v-tab-item>

                        <!-- Vehicle Inspection -->
                        <v-tab-item eager>
                            <div class="text-h6 mb-4">Vehicle Inspection</div>
                            <!-- Header checkboxes -->
                            <v-row>
                                <v-col cols="6" sm="4">
                                    <div class="caption text-left grey--text text--darken-1">Staff Turnover</div>
                                    <v-checkbox v-model="jobcard.vehicleInspection.staffTurnoverNewStart" label="New Start" hide-details dense class="mt-1" />
                                    <v-checkbox v-model="jobcard.vehicleInspection.staffTurnoverTermination" label="Termination" hide-details dense class="mt-0" />
                                </v-col>
                                <v-col cols="6" sm="4">
                                    <div class="caption text-left grey--text text--darken-1">Existing Staff</div>
                                    <v-checkbox v-model="jobcard.vehicleInspection.existingStaffIssued" label="Issued" hide-details dense class="mt-0" />
                                    <v-checkbox v-model="jobcard.vehicleInspection.existingStaffReceived" label="Received" hide-details dense class="mt-0" />
                                </v-col>
                                <v-col cols="6" sm="4">
                                    <div class="caption text-left grey--text text--darken-1">Vehicle Inspections</div>
                                    <v-checkbox v-model="jobcard.vehicleInspection.vehicleInspectionsMonthly" label="Monthly" hide-details dense class="mt-1" />
                                    <v-checkbox v-model="jobcard.vehicleInspection.vehicleInspectionsQuarterly" label="Quarterly" hide-details dense class="mt-0" />
                                </v-col>
                            </v-row>
                        
                            <!-- Text inputs -->
                            <v-row>
                                <v-col cols="12" sm="4">
                                    <v-dialog v-model="inspectionDatePickerOpen" width="290px">
                                        <template #activator="{ on, attrs }">
                                            <v-text-field
                                                :value="formattedDate(jobcard.vehicleInspection.date)"
                                                :rules="requiredInspection"
                                                label="Date"
                                                prepend-inner-icon="mdi-calendar"
                                                readonly
                                                outlined
                                                dense
                                                hide-details
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            v-model="jobcard.vehicleInspection.date"
                                            no-title
                                            full-width
                                            @input="inspectionDatePickerOpen = false"
                                        >
                                            <v-spacer />
                                            <v-btn text color="primary" @click="inspectionDatePickerOpen = false">Cancel</v-btn>
                                        </v-date-picker>
                                    </v-dialog>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        v-model="jobcard.vehicleInspection.regNr"
                                        :rules="requiredInspection"
                                        label="Reg. Nr"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        v-model.number="jobcard.vehicleInspection.kilometres"
                                        :rules="requiredInspection"
                                        type="number"
                                        label="KM's"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        v-model="jobcard.vehicleInspection.name"
                                        :rules="requiredInspection"
                                        label="Name"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        v-model="jobcard.vehicleInspection.coNr"
                                        :rules="requiredInspection"
                                        label="Co. Nr"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        v-model="jobcard.vehicleInspection.licDiscExp"
                                        :rules="requiredInspection"
                                        label="Lic. Disc Exp."
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </v-col>
                            </v-row>

                            <!-- Inspection checklist -->
                            <v-row no-gutters class="mt-6">
                                <v-col cols="12" sm="6">
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Vehicle Clean</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.vehicleClean"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Park Lights</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.parkLights"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Head Lights</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.headLights"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Indicators (Front)</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.indicatorsFront"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Brake Lights</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.brakeLights"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Reverse Lights</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.reverseLights"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Indicators (Back)</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.indicatorsBack"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Tyres</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.tyres"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Hubcaps / Wheel Caps</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.hubcapsWheelCaps"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Side View Mirrors</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.sideViewMirrors"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Windscreen</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.windscreen"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Fuel Card</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.fuelCard"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Oil Level</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.oilLevel"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Water Level</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.waterLevel"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Aerial</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.aerial"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Hooter</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.hooter"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Start Inhibit</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.startInhibit"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Spare Wheel</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.spareWheel"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Jack + Handle + Spanner</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.jackHandleSpanner"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Emergency Triangle</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.emergencyTriangle"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Toolbox</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.toolbox"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Tonneau Cover – Good Condition</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.tonneauCover"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Vehicle Interior &amp; Exterior Neat</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.interiorExteriorNeat"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Vehicle Free of Dents and Scratches</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.freeDentsScratches"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                    <div class="d-flex align-center pa-2">
                                        <span class="body-2">Branding</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.vehicleInspection.branding"
                                            :rules="requiredInspection"
                                            row
                                            dense
                                            hide-details
                                            class="mt-0 flex-shrink-0 checklist-radio"
                                        >
                                            <v-radio :value="true"><template #label><v-icon color="success">mdi-check</v-icon></template></v-radio>
                                            <v-radio :value="false"><template #label><v-icon color="error">mdi-close</v-icon></template></v-radio>
                                        </v-radio-group>
                                    </div>
                                    <v-divider />
                                </v-col>
                            </v-row>

                            <!-- Dents, Damages & Scratches -->
                            <div class="damage-table">
                                <div class="subtitle-2 mt-6 mb-2">Description of Dents, Damages &amp; Scratches</div>
                            
                                <v-simple-table dense>
                                    <thead>
                                        <tr>
                                            <th class="text-left">Description</th>
                                            <th class="text-center" style="width: 60px">Old</th>
                                            <th class="text-center" style="width: 60px">New</th>
                                            <th class="text-center" style="width: 70px">Minor</th>
                                            <th class="text-center" style="width: 70px">Serious</th>
                                            <th style="width: 36px"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(entry, index) in jobcard.vehicleInspection.damageEntries" :key="index">
                                            <td>
                                                <v-text-field
                                                    v-model="entry.description"
                                                    :rules="requiredInspection"
                                                    dense
                                                    hide-details
                                                    outlined
                                                    flat
                                                    solo
                                                    class="my-2"
                                                    placeholder="Description of damage"
                                                />
                                            </td>
                                            <td class="text-center">
                                                <v-simple-checkbox v-model="entry.isOld" color="primary" hide-details :ripple="false" />
                                            </td>
                                            <td class="text-center">
                                                <v-simple-checkbox v-model="entry.isNew" color="primary" hide-details :ripple="false" />
                                            </td>
                                            <td class="text-center">
                                                <v-simple-checkbox v-model="entry.isMinor" color="primary" hide-details :ripple="false" />
                                            </td>
                                            <td class="text-center">
                                                <v-simple-checkbox v-model="entry.isSerious" color="primary" hide-details :ripple="false" />
                                            </td>
                                            <td>
                                                <v-btn icon x-small @click="removeDamageEntry(index)">
                                                    <v-icon>mdi-delete-outline</v-icon>
                                                </v-btn>
                                            </td>
                                        </tr>
                                        <tr v-if="jobcard.vehicleInspection.damageEntries.length === 0">
                                            <td colspan="6" class="text-center grey--text caption py-3">
                                                No entries — click "Add Row" to add one.
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            
                                <v-btn small text color="primary" class="mt-2 mb-4" @click="addDamageEntry">
                                    <v-icon small left>mdi-plus</v-icon>Add Row
                                </v-btn>
                            </div>

                            <v-textarea
                                v-model="jobcard.vehicleInspection.additionalComments"
                                label="Additional comments"
                                outlined
                                dense
                                rows="3"
                                no-resize
                                hide-details
                            />
                        </v-tab-item>

                        <!-- Quality Checks-->
                        <v-tab-item eager>
                            <div class="text-h6 mb-4">Quality Checks</div>
                            <v-row no-gutters>
                                <v-col cols="12">
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Service overdue</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.serviceOverdue"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                                <template v-if="jobcard.qualityCheck.serviceOverdue">
                                    <v-col cols="12">
                                        <div class="px-2 py-3">
                                            <v-textarea
                                                v-model="jobcard.qualityCheck.serviceOverdueReason"
                                                :rules="requiredInspection"
                                                label="Reason overdue"
                                                outlined
                                                dense
                                                rows="2"
                                                no-resize
                                                hide-details
                                            />
                                        </div>
                                    </v-col>
                                </template>
                            
                                <v-col cols="12">
                                    <v-divider />
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Damages noted</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.damagesNoted"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                                <template v-if="jobcard.qualityCheck.damagesNoted">
                                    <v-col cols="12">
                                        <div class="d-flex align-start px-2 py-3">
                                            <span class="body-2 text-left">Has it been reported?</span>
                                            <v-spacer />
                                            <v-radio-group
                                                v-model="jobcard.qualityCheck.damagesReported"
                                                :rules="requiredInspection"
                                                row
                                                hide-details
                                                class="mt-0 flex-shrink-0"
                                                style="min-width: 140px"
                                            >
                                                <v-radio label="Yes" :value="true" />
                                                <v-radio label="No" :value="false" />
                                            </v-radio-group>
                                        </div>
                                    </v-col>
                                
                                    <v-col cols="12">
                                        <div class="d-flex align-start px-2 py-3">
                                            <span class="body-2 text-left">Claim form, SAPS report and AOD in order?</span>
                                            <v-spacer />
                                            <v-radio-group
                                                v-model="jobcard.qualityCheck.damagesClaimDocs"
                                                :rules="requiredInspection"
                                                row
                                                hide-details
                                                class="mt-0 flex-shrink-0"
                                                style="min-width: 140px"
                                            >
                                                <v-radio label="Yes" :value="true" />
                                                <v-radio label="No" :value="false" />
                                            </v-radio-group>
                                        </div>
                                    </v-col>
                                
                                    <v-col cols="12">
                                        <div class="px-2 py-3">
                                            <v-textarea
                                                v-model="jobcard.qualityCheck.damagesDescription"
                                                :rules="requiredInspection"
                                                label="Damage description"
                                                outlined
                                                dense
                                                rows="2"
                                                no-resize
                                                hide-details
                                            />
                                        </div>
                                    </v-col>
                                </template>
                            
                                <v-col cols="12">
                                    <v-divider />
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Are all hubcaps on the vehicle?</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.allHubcapsPresent"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider />
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Licence disc renewed?</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.licenceDiscRenewed"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider />
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Is the windscreen cracked?</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.windscreenCracked"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider />
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Is the tonneau cover still in good condition?</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.tonneauCoverGoodCondition"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider />
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Is there a Toolbox fitted?</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.toolboxFitted"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider />
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Are there any vinyl repairs that need to be done?</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.vinylRepairsNeeded"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <v-divider />
                                    <div class="d-flex align-start px-2 py-3">
                                        <span class="body-2 text-left">Is the vehicle clean inside?</span>
                                        <v-spacer />
                                        <v-radio-group
                                            v-model="jobcard.qualityCheck.vehicleCleanInside"
                                            :rules="requiredInspection"
                                            row
                                            hide-details
                                            class="mt-0 flex-shrink-0"
                                            style="min-width: 140px"
                                        >
                                            <v-radio label="Yes" :value="true" />
                                            <v-radio label="No" :value="false" />
                                        </v-radio-group>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-tab-item>

                        <!-- Tyre Replacement & Checks -->
                        <v-tab-item eager>
                            <div class="text-h6 mb-4">Tyre Replacement &amp; Checks</div>
                            <div class="tyre-table">
                                <v-simple-table dense class="mt-3">
                                    <thead>
                                        <tr>
                                            <th class="text-center" style="min-width: 120px"></th>
                                            <th class="text-center" style="min-width: 110px">Side Wall Damage</th>
                                            <th class="text-center" style="min-width: 120px">Current Tyre Brand</th>
                                            <th class="text-center" style="min-width: 120px">New Tyre Brand</th>
                                            <th class="text-center" style="min-width: 110px">Last Repl Date</th>
                                            <th class="text-center" style="min-width: 100px">Last Repl KM</th>
                                            <th class="text-center" style="min-width: 110px">Kilometres Done</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="tyre in jobcard.tyreChecks" :key="tyre.position">
                                            <th scope="row" class="text-center">
                                                {{ tyre.position }}
                                            </th>
                                            <td>
                                                <v-text-field
                                                    v-model="tyre.sideWallDamage"
                                                    :rules="requiredTyres"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    flat
                                                    solo
                                                    class="my-2"
                                                    placeholder="None"
                                                />
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model="tyre.brandCurrent"
                                                    :rules="requiredTyres"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    flat
                                                    solo
                                                    class="my-2"
                                                    placeholder="Current Brand"
                                                />
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model="tyre.brandNew"
                                                    :rules="requiredTyres"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    flat
                                                    solo
                                                    class="my-2"
                                                    placeholder="New Brand"
                                                />
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model="tyre.lastReplDate"
                                                    :rules="requiredTyres"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    flat
                                                    solo
                                                    class="my-2"
                                                    placeholder="DD/MM/YYYY"
                                                />
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model.number="tyre.lastReplKm"
                                                    :rules="requiredTyres"
                                                    type="number"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    flat
                                                    solo
                                                    class="my-2"
                                                    placeholder="Last Repl KM"
                                                />
                                            </td>
                                            <td>
                                                <v-text-field
                                                    v-model.number="tyre.kilometresDone"
                                                    :rules="requiredTyres"
                                                    type="number"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    flat
                                                    solo
                                                    class="my-2"
                                                    placeholder="KM Done"
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </div>
                        </v-tab-item>

                        <!-- Battery Details -->
                        <v-tab-item eager>
                            <div class="text-h6 mb-4">Battery Details</div>
                            <div class="battery-table">
                                <v-simple-table dense class="mt-3">
                                    <tbody>
                                        <tr>
                                            <td class="body-2 text-right">Finance Method</td>
                                            <td>
                                                <v-select
                                                    v-model="jobcard.batteryDetails.financeMethod"
                                                    :items="financeMethodTypes"
                                                    :rules="requiredBattery"
                                                    outlined
                                                    dense
                                                    hide-details
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Battery Name</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.batteryName" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Voltage before test</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.voltageBeforeTest" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Was battery replaced before?</td>
                                            <td>
                                                <v-radio-group v-model="jobcard.batteryDetails.replacedBefore" :rules="requiredBattery" row hide-details class="mt-0">
                                                    <v-radio label="Yes" :value="true" />
                                                    <v-radio label="No" :value="false" />
                                                </v-radio-group>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Ampere battery was tested with</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.ampereTestedWith" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Battery test duration</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.testDuration" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Battery final voltage reading</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.finalVoltageReading" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Current draw done?</td>
                                            <td>
                                                <v-radio-group v-model="jobcard.batteryDetails.currentDrawDone" :rules="requiredBattery" row hide-details class="mt-0">
                                                    <v-radio label="Yes" :value="true" />
                                                    <v-radio label="No" :value="false" />
                                                </v-radio-group>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Current draw reading</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.currentDrawReading" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Number of units in vehicle</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.numberOfUnits" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Battery is serviceable?</td>
                                            <td>
                                                <v-radio-group v-model="jobcard.batteryDetails.isServiceable" :rules="requiredBattery" row hide-details class="mt-0">
                                                    <v-radio label="Yes" :value="true" />
                                                    <v-radio label="No" :value="false" />
                                                </v-radio-group>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Battery tested by</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.testedBy" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Reason battery was replaced</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.replacementReason" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                        <tr>
                                            <td class="body-2 text-right">Battery replacement cost</td>
                                            <td><v-text-field v-model="jobcard.batteryDetails.replacementCost" :rules="requiredBattery" outlined dense hide-details /></td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </div>
                        </v-tab-item>

                        <!-- Uploads -->
                        <v-tab-item eager>
                            <div class="text-h6 mb-4">Uploads</div>
                            <v-alert v-if="attachmentsValidationError" type="error" dense text class="mb-4">
                                At least one attachment is required.
                            </v-alert>
                            <v-file-input
                                v-model="attachmentsFileInput"
                                label="Select pictures to upload"
                                accept="image/*"
                                multiple
                                counter
                                outlined
                                dense
                                prepend-icon=""
                                prepend-inner-icon="mdi-camera-plus"
                                chips
                                show-size
                                hide-details
                                class="mb-6"
                                @change="onAttachmentSelected"
                            />
                            
                            <v-card v-if="attachmentsPreview.length > 0" outlined class="mb-3">
                                <v-card-title class="body-2 grey--text text--darken-1">
                                    Pending uploads
                                </v-card-title>
                                <v-card-text>
                                    <v-row class="mb-6">
                                        <v-col
                                            v-for="(preview, index) in attachmentsPreview"
                                            :key="preview.url"
                                            cols="6"
                                            sm="4"
                                            md="3"
                                        >
                                            <v-card outlined>
                                                <v-img
                                                    v-if="!preview.loading"
                                                    :src="preview.url"
                                                    aspect-ratio="1"
                                                    contain
                                                    class="grey lighten-4"
                                                />
                                                <div
                                                    v-else
                                                    class="grey lighten-4 d-flex align-center justify-center"
                                                    style="aspect-ratio: 1"
                                                >
                                                    <v-progress-circular indeterminate color="primary" size="32" />
                                                </div>
                                                <v-card-actions class="pa-1 caption text-truncate">
                                                    <span class="text-truncate" style="max-width: 120px">{{ preview.name }}</span>
                                                    <v-spacer />
                                                    <v-btn icon small color="error" :disabled="preview.loading" @click="removePendingAttachment(index)">
                                                        <v-icon small>mdi-delete</v-icon>
                                                    </v-btn>
                                                </v-card-actions>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                            
                            <v-card v-if="jobcard.attachmentNames && jobcard.attachmentNames.length > 0" outlined>
                                <v-card-title class="body-2 grey--text text--darken-1">
                                    Uploaded pictures
                                </v-card-title>
                                <v-card-text>
                                    <v-row>
                                        <v-col
                                            v-for="(attachmentName, index) in jobcard.attachmentNames"
                                            :key="index"
                                            cols="6"
                                            sm="4"
                                            md="3"
                                        >
                                            <v-card outlined>
                                                <v-img
                                                    :src="getAttachmentUrl(attachmentName)"
                                                    aspect-ratio="1"
                                                    contain
                                                    class="grey lighten-4"
                                                >
                                                    <template #placeholder>
                                                        <v-row class="fill-height" align="center" justify="center">
                                                            <v-progress-circular indeterminate color="grey lighten-1" />
                                                        </v-row>
                                                    </template>
                                                </v-img>
                                                <v-card-actions class="pa-1">
                                                    <v-btn icon small :href="getAttachmentUrl(attachmentName)" target="_blank">
                                                        <v-icon small>mdi-download</v-icon>
                                                    </v-btn>
                                                    <v-btn icon small @click="deleteAttachment(attachmentName)">
                                                        <v-icon small>mdi-delete</v-icon>
                                                    </v-btn>
                                                    <v-spacer />
                                                </v-card-actions>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        
                            <div v-else-if="attachments && attachments.length === 0" class="text-center grey--text py-8">
                                No attachments yet
                            </div>
                        </v-tab-item>

                        <!-- Costing -->
                        <v-tab-item eager>
                            <div class="text-h6 mb-4">Costing</div>

                            <v-tabs
                                v-model="costingViewTab"
                                background-color="transparent"
                                class="mb-4"
                                show-arrows
                            >
                                <v-tab>Workshop Costing</v-tab>
                                <v-tab>Manual Costing</v-tab>
                            </v-tabs>

                            <div v-show="showWorkshopCostingTab">
                                <template v-if="visibleQuotes.length > 0">
                                    <template v-if="visibleQuotes.length > 1">
                                        <v-btn-toggle v-model="costingTab" mandatory class="mb-4 d-none d-sm-flex">
                                            <v-btn v-for="(quote, i) in visibleQuotes" :key="quote.uniqueId" text class="flex-grow-1">
                                                Quote {{ i + 1 }} - ({{ quoteStatusById[quote.quoteStatus] }})
                                            </v-btn>
                                        </v-btn-toggle>
                                        <v-select
                                            v-model="costingTab"
                                            :items="visibleQuotes.map((q, i) => ({ text: 'Quote ' + (i + 1) + ' - ' + quoteStatusById[q.quoteStatus], value: i }))"
                                            outlined
                                            dense
                                            hide-details
                                            class="mb-4 d-sm-none"
                                        />
                                    </template>
                                    <div v-else class="mb-4">
                                        <v-chip color="primary" label>
                                            {{ quoteStatusById[visibleQuotes[0].quoteStatus] }}
                                        </v-chip>
                                    </div>

                                    <v-tabs-items v-model="costingTab">
                                        <v-tab-item v-for="quote in visibleQuotes" :key="quote.uniqueId">
                                            <div v-if="getInvoiceState(quote.uniqueId).loading" class="d-flex align-center justify-center py-8">
                                                <v-progress-circular indeterminate color="primary" size="48" />
                                            </div>
                                            <v-alert v-else-if="getInvoiceState(quote.uniqueId).error" type="error" dense text class="mb-0">
                                                Failed to load invoice.
                                            </v-alert>
                                            <template v-else>
                                                <img
                                                    v-for="(img, j) in getInvoiceState(quote.uniqueId).images"
                                                    :key="j"
                                                    :src="`data:image/png;base64,${img}`"
                                                    style="width: 100%; display: block; margin-bottom: 8px; border: 1px solid rgba(0,0,0,0.12); border-radius: 4px;"
                                                />
                                                <div v-if="getInvoiceState(quote.uniqueId).images.length === 0" class="text-center grey--text body-2 py-8">
                                                    No invoice available.
                                                </div>
                                            </template>
                                        </v-tab-item>
                                    </v-tabs-items>
                                </template>

                                <div v-else class="text-center grey--text body-2 py-8">
                                    No quotes submitted yet.
                                </div>
                            </div>

                            <div v-show="showManualCostingTab">
                                <div class="manual-costing-table">
                                    <v-simple-table dense>
                                        <thead>
                                            <tr>
                                                <th class="text-left" style="min-width: 110px">Part Code</th>
                                                <th class="text-left" style="min-width: 160px">Description</th>
                                                <th class="text-left" style="min-width: 140px">Supplier</th>
                                                <th class="text-left" style="min-width: 70px">Qty</th>
                                                <th class="text-left" style="min-width: 100px">Price (Excl)</th>
                                                <th class="text-left" style="min-width: 100px">Price (Incl)</th>
                                                <th class="text-center" style="min-width: 70px">No VAT</th>
                                                <th class="text-right" style="min-width: 100px">Total (Excl)</th>
                                                <th style="width: 36px"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(entry, index) in jobcard.manualCostings" :key="index">
                                                <td>
                                                    <v-text-field
                                                        v-model="entry.partCode"
                                                        dense
                                                        outlined
                                                        hide-details
                                                        flat
                                                        solo
                                                        class="my-2"
                                                        placeholder="Part code"
                                                    />
                                                </td>
                                                <td>
                                                    <v-text-field
                                                        v-model="entry.description"
                                                        :rules="required"
                                                        dense
                                                        outlined
                                                        hide-details
                                                        flat
                                                        solo
                                                        class="my-2"
                                                        placeholder="Description"
                                                    />
                                                </td>
                                                <td>
                                                    <v-text-field
                                                        v-model="entry.supplierName"
                                                        dense
                                                        outlined
                                                        hide-details
                                                        flat
                                                        solo
                                                        class="my-2"
                                                        placeholder="Supplier"
                                                    />
                                                </td>
                                                <td>
                                                    <v-text-field
                                                        v-model.number="entry.quantity"
                                                        :rules="requiredPositiveNumber"
                                                        type="number"
                                                        dense
                                                        outlined
                                                        hide-details
                                                        flat
                                                        solo
                                                        class="my-2"
                                                        placeholder="1"
                                                    />
                                                </td>
                                                <td>
                                                    <v-text-field
                                                        v-model.number="entry.priceExcl"
                                                        :rules="requiredPositiveNumber"
                                                        type="number"
                                                        dense
                                                        outlined
                                                        hide-details
                                                        flat
                                                        solo
                                                        class="my-2"
                                                        placeholder="0.00"
                                                        @input="onManualCostingPriceExclChanged(entry)"
                                                    />
                                                </td>
                                                <td>
                                                    <v-text-field
                                                        v-model.number="entry.priceIncl"
                                                        :rules="requiredPositiveNumber"
                                                        type="number"
                                                        dense
                                                        outlined
                                                        hide-details
                                                        flat
                                                        solo
                                                        class="my-2"
                                                        placeholder="0.00"
                                                        @input="onManualCostingPriceInclChanged(entry)"
                                                    />
                                                </td>
                                                <td class="text-center">
                                                    <v-simple-checkbox
                                                        v-model="entry.noVat"
                                                        color="primary"
                                                        hide-details
                                                        :ripple="false"
                                                        @input="onManualCostingNoVatChanged(entry)"
                                                    />
                                                </td>
                                                <td class="text-right">
                                                    {{ financeHelper.formatNumberAsCurrency(manualCostingRowTotalExcl(entry)) }}
                                                </td>
                                                <td>
                                                    <v-btn icon x-small @click="removeManualCosting(index)">
                                                        <v-icon>mdi-delete-outline</v-icon>
                                                    </v-btn>
                                                </td>
                                            </tr>
                                            <tr v-if="jobcard.manualCostings.length === 0">
                                                <td colspan="9" class="text-center grey--text caption py-3">
                                                    No manual costs added — click "Add Row" to add one.
                                                </td>
                                            </tr>
                                        </tbody>
                                    </v-simple-table>

                                    <div class="d-flex align-center mt-2">
                                        <v-btn small text color="primary" @click="addManualCosting">
                                            <v-icon small left>mdi-plus</v-icon>Add Row
                                        </v-btn>
                                        <v-spacer />
                                        <div v-if="jobcard.manualCostings.length > 0" class="text-right">
                                            <div class="caption grey--text text--darken-1">
                                                Total (Excl): {{ financeHelper.formatNumberAsCurrency(manualCostingsTotalExcl) }}
                                            </div>
                                            <div class="caption grey--text text--darken-1">
                                                VAT: {{ financeHelper.formatNumberAsCurrency(manualCostingsTotalVat) }}
                                            </div>
                                            <div class="subtitle-2">
                                                Total (Incl): {{ financeHelper.formatNumberAsCurrency(manualCostingsTotalIncl) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </v-tab-item>

                        <!-- Approvals -->
                        <v-tab-item eager>
                            <div class="text-h6 mb-3">Approvals</div>

                            <div v-if="jobcard.signatures.length === 0" class="text-center grey--text body-2 py-4">
                                No approvals added yet — click "Add Approver" below.
                            </div>

                            <v-row dense>
                                <v-col
                                    v-for="(approver, i) in jobcard.signatures"
                                    :key="i"
                                    cols="12"
                                    md="6"
                                >
                                    <v-card outlined class="pa-2">
                                        <div class="d-flex justify-end">
                                            <v-btn icon x-small @click="removeApprover(i)">
                                                <v-icon small color="grey">mdi-delete-outline</v-icon>
                                            </v-btn>
                                        </div>
                                        <v-row no-gutters>
                                            <!-- Left: name, approver, comment -->
                                            <v-col cols="12" sm="6" class="pa-2">
                                                <v-select
                                                    v-model="approver.type"
                                                    :items="approverTypes"
                                                    :rules="required"
                                                    placeholder="Signature Type"
                                                    dense
                                                    hide-details
                                                    outlined
                                                    :disabled="approver.id > 0"
                                                    class="mb-3"
                                                />

                                                <v-text-field
                                                    v-model="approver.signedBy"
                                                    :rules="required"
                                                    placeholder="Name"
                                                    dense
                                                    hide-details
                                                    flat
                                                    outlined
                                                    :disabled="approver.id > 0"
                                                    class="approver-name-field mb-3"
                                                />

                                                <v-textarea
                                                    v-model="approver.comment"
                                                    outlined
                                                    dense
                                                    rows="2"
                                                    no-resize
                                                    hide-details
                                                    placeholder="Comment"
                                                    :disabled="approver.id > 0"
                                                    class="mb-3"
                                                />
                                            </v-col>
                                        
                                            <!-- Right: signature, date -->
                                            <v-col cols="12" sm="6" class="pa-2 d-flex flex-column">
                                                <div
                                                    v-if="approver.id === 0"
                                                    class="empty-signature-container flex-grow-1 mb-3"
                                                    @click="openCaptureSignatureDialog(approver)"
                                                >
                                                    <v-img
                                                        v-if="approver.signature"
                                                        :src="approver.signature"
                                                        contain
                                                        height="80"
                                                    />
                                                    <template v-else>
                                                        <v-icon size="24" color="grey lighten-1">mdi-draw</v-icon>
                                                        <div class="grey--text" style="font-size: 10px">Click to sign</div>
                                                    </template>
                                                </div>
                                                <div
                                                    v-else
                                                    class="signature-container flex-grow-1 mb-3"
                                                >
                                                    <v-img
                                                        :src="approver.signature"
                                                        contain
                                                        height="80"
                                                    />
                                                </div>

                                                <v-dialog v-if="approver.id === 0" v-model="approver.datePickerOpen" width="290px">
                                                    <template #activator="{ on, attrs }">
                                                        <v-text-field
                                                            :value="formattedDate(approver.signedDate)"
                                                            placeholder="DD/MM/YYYY"
                                                            prepend-inner-icon="mdi-calendar"
                                                            readonly
                                                            outlined
                                                            dense
                                                            hide-details
                                                            v-bind="attrs"
                                                            v-on="on"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="approver.signedDate"
                                                        no-title
                                                        full-width
                                                        @input="approver.datePickerOpen = false"
                                                    />
                                                </v-dialog>
                                                <template v-else>
                                                    <v-text-field
                                                        :value="formattedDate(approver.signedDate)"
                                                        placeholder="DD/MM/YYYY"
                                                        prepend-inner-icon="mdi-calendar"
                                                        readonly
                                                        outlined
                                                        dense
                                                        hide-details
                                                        disabled
                                                    />
                                                </template>
                                            </v-col>
                                        </v-row>
                                    
                                        <v-row no-gutters>
                                            <v-col class="pa-2 pt-0">
                                                <v-radio-group
                                                    v-model="approver.isApproved"
                                                    :rules="required"
                                                    :disabled="approver.id > 0"
                                                    row
                                                    dense
                                                    hide-details
                                                    class="checklist-radio"
                                                >
                                                    <v-radio :value="true" class="flex-grow-1" color="green"><template #label><v-icon color="success">mdi-check</v-icon>APPROVED</template></v-radio>
                                                    <v-radio :value="false" class="flex-grow-1" color="red"><template #label><v-icon color="error">mdi-close</v-icon>DECLINED</template></v-radio>
                                                </v-radio-group>
                                            </v-col>
                                        </v-row>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <v-btn outlined color="primary" small class="mt-3" @click="addApprover">
                                <v-icon small left>mdi-plus</v-icon>Add Approver
                            </v-btn>
                        </v-tab-item>
                    </v-tabs-items>
                </v-form>
            </v-card-text>

            <template v-if="screenState === 'form'">
                <v-divider />
            
                <v-card-actions class="pa-3 d-block">
                    <v-alert v-if="formValidationError" type="error" dense text class="mb-3">
                        Please complete all required fields. Use the tabs above to find highlighted fields.
                    </v-alert>
                
                    <v-alert v-if="saveError" type="error" dense text class="mb-3">
                        Failed to save jobcard. Please try again.
                    </v-alert>

                    <!-- Row 1: navigation — mobile only -->
                    <div class="d-flex d-sm-none align-center mb-2" style="gap: 8px">
                        <v-btn v-if="activeTab > 0" text @click="activeTab--">
                            <v-icon left>mdi-chevron-left</v-icon>Previous
                        </v-btn>
                        <v-spacer />
                        <v-btn v-if="activeTab < 7" text color="primary" @click="activeTab++">
                            Next<v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>

                    <!-- Row 2: action buttons — on desktop also contains Previous/Next -->
                    <div class="d-flex align-center" style="gap: 8px">
                        <v-btn v-if="activeTab > 0" text class="d-none d-sm-flex" @click="activeTab--">
                            <v-icon left>mdi-chevron-left</v-icon>Previous
                        </v-btn>
                        <v-spacer />
                        <v-btn v-if="jobcard.id > 0" color="secondary" @click="openGeneratePDFDialog" :loading="loading">
                            <v-icon left>mdi-printer</v-icon> Save & Print
                        </v-btn>
                        <v-btn color="primary" :loading="loading" @click="saveAndCloseJobcard">
                            <v-icon left>mdi-floppy</v-icon> Save & Close
                        </v-btn>
                        <v-btn v-if="activeTab < 7" text color="primary" class="d-none d-sm-flex" @click="activeTab++">
                            Next<v-icon right>mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                </v-card-actions>
            </template>
        </v-card>
    </v-dialog>
</template>

<script>
import { pdfService, quoteRequestService } from '@/services';
import { openDialog } from '@/helpers/dialog';
import {
    quoteRequestJobcardSignatureType,
    quoteRequestJobcardType,
    quoteStatuses,
} from '@/helpers/constants';
import { imageHelper, dateHelper, financeHelper } from '@/helpers';
import CaptureSignatureDialog from '@/components/dialogs/capture-signature-dialog.vue';
import PrintQuoteRequestJobcardDialog from '@/components/dialogs/print-quote-request-jobcard-dialog.vue';
import ConfirmDialog from '@/components/dialogs/confirm-dialog.vue';

export default {
    name: 'capture-quote-request-jobcard-dialog',
    props: {
        titleProp: {
            type: String,
            default: 'Capture Jobcard',
        },
        vehicleMappingProp: {
            type: Object,
            default: () => null,
            required: true,
        },
        jobcardIdProp: {
            type: Number,
            default: null,
        },
        quoteRequestIdProp: {
            type: Number,
            default: null,
        },
        showAllVehicleJobcardsProp: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        open: true,
        loading: false,
        screenState: 'form',
        existingJobcards: [],
        activeTab: 0,
        costingViewTab: 0,
        costingTab: 0,
        costingData: {},
        dateRequestedPickerOpen: false,
        inspectionDatePickerOpen: false,
        formValidationError: false,
        attachmentsValidationError: false,
        saveError: false,
        loadError: false,
        attachmentsFileInput: null,
        attachments: [],
        attachmentsPreview: [],
        jobcardTypes: Object.values(quoteRequestJobcardType)
            .filter(v => typeof v === 'number')
            .map(v => ({ text: quoteRequestJobcardType[v], value: v })),
        approverTypes: Object.values(quoteRequestJobcardSignatureType)
            .filter(v => typeof v === 'number')
            .map(v => ({ text: quoteRequestJobcardSignatureType[v], value: v })),
        financeMethodTypes: [
            'Owned',
            'FML',
            'Financed',
        ],
        jobcard: {
            id: 0,
            vehicleId: null,
            quoteRequestId: null, // This field is only on the model and used for linking to a quote request
            jobcardDetails: {
                type: null,
                ctaRef: '',
                requestedBy: '',
                fleetController: '',
                branch: '',
                driver: '',
                dateRequested: dateHelper.formatDateCustom(new Date(), 'YYYY-MM-DD'),
                registrationNumber: '',
                kilometres: '',
                make: '',
                model: '',
                lastService: '',
                lastServiceDate: null,
                gpsTested: null,
                didTagTested: null,
                fuelTested: null,
                reason: '',
            },
            qualityCheck: {
                serviceOverdue: null,
                serviceOverdueReason: '',
                damagesNoted: null,
                damagesDescription: '',
                damagesReported: null,
                damagesClaimDocs: null,
                allHubcapsPresent: null,
                licenceDiscRenewed: null,
                windscreenCracked: null,
                tonneauCoverGoodCondition: null,
                toolboxFitted: null,
                vinylRepairsNeeded: null,
                vehicleCleanInside: null,
            },
            vehicleInspection: {
                staffTurnoverNewStart: false,
                staffTurnoverTermination: false,
                existingStaffIssued: false,
                existingStaffReceived: false,
                vehicleInspectionsMonthly: false,
                vehicleInspectionsQuarterly: false,
                date: dateHelper.formatDateCustom(new Date(), 'YYYY-MM-DD'),
                regNr: '',
                kilometres: '',
                name: '',
                coNr: '',
                licDiscExp: '',
                vehicleClean: null,
                parkLights: null,
                headLights: null,
                indicatorsFront: null,
                brakeLights: null,
                reverseLights: null,
                indicatorsBack: null,
                tyres: null,
                hubcapsWheelCaps: null,
                sideViewMirrors: null,
                windscreen: null,
                fuelCard: null,
                oilLevel: null,
                waterLevel: null,
                aerial: null,
                hooter: null,
                startInhibit: null,
                spareWheel: null,
                jackHandleSpanner: null,
                emergencyTriangle: null,
                toolbox: null,
                tonneauCover: null,
                interiorExteriorNeat: null,
                freeDentsScratches: null,
                branding: null,
                damageEntries: [],
                additionalComments: '',
            },
            tyreChecks: [
                { position: 'Front Right', sideWallDamage: '', brandCurrent: '', brandNew: '', lastReplDate: '', lastReplKm: '', kilometresDone: '' },
                { position: 'Front Left', sideWallDamage: '', brandCurrent: '', brandNew: '', lastReplDate: '', lastReplKm: '', kilometresDone: '' },
                { position: 'Rear Right', sideWallDamage: '', brandCurrent: '', brandNew: '', lastReplDate: '', lastReplKm: '', kilometresDone: '' },
                { position: 'Rear Left', sideWallDamage: '', brandCurrent: '', brandNew: '', lastReplDate: '', lastReplKm: '', kilometresDone: '' },
                { position: 'Spare Wheel', sideWallDamage: '', brandCurrent: '', brandNew: '', lastReplDate: '', lastReplKm: '', kilometresDone: '' },
            ],
            batteryDetails: {
                financeMethod: null,
                batteryName: '',
                voltageBeforeTest: '',
                replacedBefore: null,
                ampereTestedWith: '',
                testDuration: '',
                finalVoltageReading: '',
                currentDrawDone: null,
                currentDrawReading: '',
                numberOfUnits: '',
                isServiceable: null,
                testedBy: '',
                replacementReason: '',
                replacementCost: '',
            },
            manualCostings: [],
            attachmentNames: null,
            signatures: [],
        },
    }),
    async created() {
        await this.loadInitialData();
    },
    computed: {
        quoteRequestJobcardType() {
            return quoteRequestJobcardType;
        },
        financeHelper() {
            return financeHelper;
        },
        quoteStatuses() {
            return quoteStatuses;
        },
        quoteStatusById() {
            return Object.values(quoteStatuses).reduce((map, s) => {
                if (s.id !== null) map[s.id] = s.label;
                return map;
            }, {});
        },
        approvedQuote() {
            const quotes = this.jobcard.quoteRequest?.quotes || [];
            return quotes.find(q => q.quoteStatus > 0);
        },
        visibleQuotes() {
            const quotes = this.jobcard.quoteRequest?.quotes || [];
            return this.approvedQuote ? quotes.filter(q => q.quoteStatus > 0) : quotes;
        },
        required() {
            return [v => (v !== null && v !== undefined && v !== '') || 'Required'];
        },
        requiredTyres() {
            return this.jobcard.jobcardDetails.type === this.quoteRequestJobcardType.TYPE
                ? this.required
                : [];
        },
        requiredBattery() {
            return this.jobcard.jobcardDetails.type === this.quoteRequestJobcardType.BATTERY
                ? this.required
                : [];
        },
        requiredInspection() {
            return (this.jobcard.jobcardDetails.type === this.quoteRequestJobcardType.INSPECTION || this.approvedQuote)
                ? this.required
                : [];
        },
        requiredPositiveNumber() {
            return [
                v => (v !== null && v !== undefined && v !== '') || 'Required',
                v => Number(v) > 0 || 'Must be greater than 0',
            ];
        },
        showWorkshopCostingTab() {
            return this.costingViewTab === 0;
        },
        showManualCostingTab() {
            return this.costingViewTab === 1;
        },
        manualCostingsTotalExcl() {
            return this.jobcard.manualCostings.reduce((sum, entry) => sum + this.manualCostingRowTotalExcl(entry), 0);
        },
        manualCostingsTotalIncl() {
            return this.jobcard.manualCostings.reduce((sum, entry) => sum + this.manualCostingRowTotalIncl(entry), 0);
        },
        manualCostingsTotalVat() {
            return this.manualCostingsTotalIncl - this.manualCostingsTotalExcl;
        },
    },
    methods: {
        async loadInitialData() {
            if (!this.jobcard.vehicleId && this.vehicleMappingProp?.vehicleId) {
                this.jobcard.vehicleId = this.vehicleMappingProp.vehicleId;
            }
            this.screenState = 'loading';
            this.loadError = false;

            if (this.jobcardIdProp > 0) {
                await this.loadJobcardById(this.jobcardIdProp);
            }
            else if (this.quoteRequestIdProp > 0) {
                await this.loadJobcardByQuoteRequestId(this.quoteRequestIdProp);
            }

            if (this.loadError) {
                this.screenState = 'error';
                return;
            }

            // Fetch all vehicle jobcards if no jobcard is linked to the vehicle yet
            if (!this.jobcard?.id) {
                await this.loadJobcardsByVehicleId(this.jobcard.vehicleId);
            }

            // Populate form data if new jobcard
            if (this.vehicleMappingProp && !this.jobcard?.id) {
                this.jobcard.jobcardDetails.kilometres = this.vehicleMappingProp.vehicleMileage ?? '';
                this.jobcard.jobcardDetails.registrationNumber = this.vehicleMappingProp.vehicleLicensePlate ?? '';
                this.jobcard.jobcardDetails.make = this.vehicleMappingProp.vehicle?.brand ?? '';
                this.jobcard.jobcardDetails.model = this.vehicleMappingProp.vehicle?.model ?? '';
                this.jobcard.vehicleInspection.kilometres = this.vehicleMappingProp.vehicleMileage ?? '';
                this.jobcard.vehicleInspection.regNr = this.vehicleMappingProp.vehicleLicensePlate ?? '';

                const selectedBranchId = this.$store.state.user.organization?.authUser?.organizationBranchId;
                const branchOptions = this.$store.state.user.organizationBranchOptions;
                const selectedBranch = branchOptions?.find(b => b.organizationBranchId === selectedBranchId);
                this.jobcard.jobcardDetails.branch = selectedBranch?.organizationBranchName ?? '';
            }

            this.screenState = this.existingJobcards.length > 0 ? 'select' : 'form';
        },
        startNewJobcard() {
            this.screenState = 'form';
        },
        setJobcard(data) {
            this._.merge(this.jobcard, data);
            this.attachmentsFileInput = null;
            this.attachments = [];
            this.attachmentsPreview.forEach(p => p.url && URL.revokeObjectURL(p.url));
            this.attachmentsPreview = [];

            // Spread into a new array so Vue 2 detects the change
            this.jobcard.vehicleInspection.damageEntries = (this.jobcard.vehicleInspection.damageEntries || []).map(e => ({ ...e }));
            this.jobcard.manualCostings = (this.jobcard.manualCostings || []).map(e => ({ ...e }));
        },
        selectJobcard(jobcard) {
            this.setJobcard(jobcard);
            this.screenState = 'form';
        },
        addDamageEntry() {
            this.jobcard.vehicleInspection.damageEntries.push({
                description: '',
                isOld: false,
                isNew: false,
                isMinor: false,
                isSerious: false,
            });
        },
        removeDamageEntry(index) {
            this.jobcard.vehicleInspection.damageEntries.splice(index, 1);
        },
        addManualCosting() {
            this.jobcard.manualCostings.push({
                partCode: '',
                description: '',
                supplierName: '',
                quantity: 1,
                priceExcl: null,
                priceIncl: null,
                noVat: false,
                lastEditedPrice: null,
            });
        },
        removeManualCosting(index) {
            this.jobcard.manualCostings.splice(index, 1);
        },
        syncManualCostingFromExcl(entry) {
            const excl = parseFloat(entry.priceExcl) || 0;
            entry.priceIncl = entry.noVat ? excl : financeHelper.addVat(excl);
        },
        syncManualCostingFromIncl(entry) {
            const incl = parseFloat(entry.priceIncl) || 0;
            entry.priceExcl = entry.noVat ? incl : financeHelper.removeVat(incl);
        },
        onManualCostingPriceExclChanged(entry) {
            entry.lastEditedPrice = 'excl';
            this.syncManualCostingFromExcl(entry);
        },
        onManualCostingPriceInclChanged(entry) {
            entry.lastEditedPrice = 'incl';
            this.syncManualCostingFromIncl(entry);
        },
        onManualCostingNoVatChanged(entry) {
            // Replicate whichever price field was last edited onto the other.
            // Falls back to excl -> incl when it's inconclusive (untouched row).
            if (entry.lastEditedPrice === 'incl') {
                this.syncManualCostingFromIncl(entry);
            }
            else {
                this.syncManualCostingFromExcl(entry);
            }
        },
        manualCostingRowTotalExcl(entry) {
            const qty = parseFloat(entry.quantity) || 0;
            const priceExcl = parseFloat(entry.priceExcl) || 0;
            return qty * priceExcl;
        },
        manualCostingRowTotalIncl(entry) {
            const qty = parseFloat(entry.quantity) || 0;
            const priceIncl = parseFloat(entry.priceIncl) || 0;
            return qty * priceIncl;
        },
        getAttachmentUrl(attachmentName) {
            return `${process.env.VUE_APP_SMC_CONTENT_URL}/quote-request-jobcard-attachments/${this.jobcard.uniqueId}/${attachmentName}`;
        },
        removePendingAttachment(index) {
            URL.revokeObjectURL(this.attachmentsPreview[index].url);
            this.attachmentsPreview.splice(index, 1);
            this.attachments.splice(index, 1);
        },
        async onAttachmentSelected(files) {
            this.loading = true;
            this.attachmentsPreview.forEach(p => p.url && URL.revokeObjectURL(p.url));
            if (!files || files.length === 0) {
                this.attachments = [];
                this.attachmentsPreview = [];
                this.loading = false;
                return;
            }
            this.attachmentsPreview = Array.from(files).map(f => ({ url: null, name: f.name, loading: true }));
            const compressedFiles = [];
            await Promise.all(Array.from(files).map(async (file, i) => {
                const compressed = await imageHelper.compressFile(file);
                compressedFiles[i] = compressed;
                this.$set(this.attachmentsPreview, i, {
                    url: URL.createObjectURL(compressed),
                    name: compressed.name,
                    loading: false,
                });
            }));
            this.attachments = compressedFiles;
            this.loading = false;
        },
        addApprover() {
            this.jobcard.signatures.push({
                id: 0,
                type: null,
                signedBy: '',
                comment: '',
                isApproved: null,
                signedDate: dateHelper.formatDateCustom(new Date(), 'YYYY-MM-DD'),
                signature: null,
                datePickerOpen: false,
            });
        },
        async removeApprover(index) {
            const confirmed = await openDialog(ConfirmDialog, {
                titleProp: 'Confirm Delete',
                descriptionProp: 'Are you sure you want to remove this approver?',
            });
            if (!confirmed) return;

            this.jobcard.signatures.splice(index, 1);
        },
        formattedDate(date) {
            return date ? dateHelper.formatDateShort(date) : '';
        },
        navigateToFirstErrorTab() {
            const firstErrorInput = this.$refs.jobcardForm.inputs.find(i => !i.valid);
            if (firstErrorInput) {
                // Walk up the Vue component tree (not DOM) — works even inside a portaled dialog
                let component = firstErrorInput;
                while (component.$parent) {
                    component = component.$parent;
                    if (component.$options.name === 'v-tab-item') {
                        const siblings = component.$parent.$children.filter(
                            c => c.$options.name === 'v-tab-item',
                        );
                        const tabIndex = siblings.indexOf(component);
                        if (tabIndex >= 0) {
                            this.activeTab = tabIndex;
                            return;
                        }
                        break;
                    }
                }
            }
            if (this.attachmentsValidationError) { this.activeTab = 5; }
        },
        async loadJobcardById(jobcardId) {
            try {
                const jobcard = await quoteRequestService.getJobcardById(jobcardId);

                if (jobcard) {
                    this.setJobcard(jobcard);
                }
            }
            catch {
                this.loadError = true;
            }
        },
        async loadJobcardByQuoteRequestId(quoteRequestId) {
            try {
                const jobcard = await quoteRequestService.getJobcardByQuoteRequestId(quoteRequestId);

                if (jobcard) {
                    this.setJobcard(jobcard);
                }
            }
            catch {
                this.loadError = true;
            }
        },
        async loadJobcardsByVehicleId(vehicleId) {
            try {
                this.existingJobcards = await quoteRequestService.getJobcardsByVehicleId(vehicleId, this.showAllVehicleJobcardsProp) || [];
            }
            catch {
                this.existingJobcards = [];
            }
        },
        getInvoiceState(uid) {
            return this.costingData[uid] || { images: [], loading: false, error: false };
        },
        loadInvoiceForCurrentTab() {
            const uid = this.visibleQuotes[this.costingTab]?.uniqueId;
            if (!uid) return;
            const state = this.costingData[uid];
            if (!state || (!state.loading && !state.error && state.images.length === 0)) {
                this.loadInvoiceImages(uid);
            }
        },
        async loadInvoiceImages(uniqueId) {
            this.$set(this.costingData, uniqueId, { images: [], loading: true, error: false });
            try {
                const config = {
                    ShowVatTotal: true,
                    ShowPartCodes: true,
                    ShowVatPerLine: true,
                    ShowExclusiveAmounts: false,
                    IncludeCustomer: true,
                    IncludeCustomerBusiness: true,
                    IncludeVehicleInfo: true,
                    IncludeBusinessInfo: true,
                    IncludeSignature: true,
                };
                const result = await pdfService.generateInvoiceImagesByUniqueId(uniqueId, config);
                this.$set(this.costingData, uniqueId, { images: result.data || [], loading: false, error: false });
            }
            catch {
                this.$set(this.costingData, uniqueId, { images: [], loading: false, error: true });
            }
        },
        async openCaptureSignatureDialog(approver) {
            const result = await openDialog(CaptureSignatureDialog);
            if (result !== null) {
                approver.signature = result.attachmentSource;
            }
        },
        validateForm() {
            this.formValidationError = false;
            this.attachmentsValidationError = false;

            const formValid = this.$refs.jobcardForm.validate();

            const isInspection = this.jobcard.jobcardDetails.type === this.quoteRequestJobcardType.INSPECTION;
            this.attachmentsValidationError = !isInspection && !(
                (this.attachments && this.attachments.length > 0)
                || (this.jobcard.attachmentNames && this.jobcard.attachmentNames.length > 0)
            );

            if (!formValid || this.attachmentsValidationError) {
                this.formValidationError = true;
                this.navigateToFirstErrorTab();
                return false;
            }

            return true;
        },
        async saveJobcard() {
            this.saveError = false;
            this.loading = true;
            try {
                // Link jobcard to quote request
                if (this.quoteRequestIdProp > 0 && !this.jobcard.quoteRequestId) {
                    this.jobcard.quoteRequestId = this.quoteRequestIdProp;
                }

                const result = await quoteRequestService.saveJobcard(
                    this.jobcard,
                    this.attachments,
                );

                if (result.status === 200) {
                    this.setJobcard(result.data);
                    return true;
                }

                this.saveError = true;
                return false;
            }
            catch {
                this.saveError = true;
                return false;
            }
            finally {
                this.loading = false;
            }
        },
        async openGeneratePDFDialog() {
            if (!this.validateForm()) return;
            const saved = await this.saveJobcard();
            if (!saved) return;

            await this.$dialog(PrintQuoteRequestJobcardDialog, {
                titleProp: 'Generate Jobcard PDF',
                uniqueIdProp: this.jobcard.uniqueId,
                jobcardTypeProp: this.jobcard.jobcardDetails.type,
            });
        },
        async saveAndCloseJobcard() {
            if (!this.validateForm()) return;
            const saved = await this.saveJobcard();
            if (!saved) return;
            this.$emit('close', this.jobcard);
        },
        async deleteAttachment(attachmentName) {
            if (!this.jobcard.id) return;

            const confirmed = await openDialog(ConfirmDialog, {
                titleProp: 'Confirm Delete',
                descriptionProp: 'Are you sure you want to delete this attachment? This cannot be undone.',
            });
            if (!confirmed) return;

            this.loading = true;
            
            try {
                const result = await quoteRequestService.deleteJobcardAttachment(
                    this.jobcard.id,
                    attachmentName,
                );
                
                if (result.status === 200) {
                    this.jobcard.attachmentNames = this.jobcard.attachmentNames
                        .filter(name => name !== attachmentName);
                }
            }
            catch (error) {
                console.error(error);
            }
            
            this.loading = false;
        },
    },
    watch: {
        activeTab(val) {
            if (val === 6) {
                this.loadInvoiceForCurrentTab();
            }
        },
        costingTab() {
            this.loadInvoiceForCurrentTab();
        },
    },
    beforeDestroy() {
        this.attachmentsPreview.forEach(p => URL.revokeObjectURL(p.url));
    },
};
</script>

<style scoped>
::v-deep .damage-table th,
::v-deep .damage-table td {
    padding: 0 6px !important;
}
::v-deep .damage-table tbody tr{
    background-color: rgba(0, 0, 0, 0.04);
}

::v-deep .tyre-table th,
::v-deep .tyre-table td {
    padding: 0 6px !important;
}
::v-deep .tyre-table tbody tr{
    background-color: rgba(0, 0, 0, 0.04);
}

::v-deep .manual-costing-table th,
::v-deep .manual-costing-table td {
    padding: 0 6px !important;
}
::v-deep .manual-costing-table tbody tr{
    background-color: rgba(0, 0, 0, 0.04);
}

::v-deep .battery-table td {
    padding: 10px 6px !important;
}
::v-deep .battery-table td:last-child {
    width: 500px;
    text-align: right;
}

::v-deep .approver-name-field input {
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    padding: 0 !important;
}

.empty-signature-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    min-height: 72px;
    padding: 8px;
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s;
}

.empty-signature-container:hover {
    border-color: rgba(0, 0, 0, 0.4);
}

.signature-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    min-height: 72px;
    padding: 8px;
    transition: border-color 0.2s, background-color 0.2s;
}

::v-deep .checklist-radio {
    &.v-input--radio-group {
        margin: 0;
        padding: 0;
    }

    .v-input__slot {
        border: 1px solid rgba(0, 0, 0, 0.23);
        border-radius: 4px;
        overflow: hidden;
        margin-bottom: 0;
    }

    .v-input--selection-controls__input {
        display: none;
        width: 0;
        margin: 0;
    }

    .v-radio {
        margin: 0 !important;
        padding: 3px 8px;
        transition: background-color 0.15s;

        &:not(:last-child) {
            border-right: 1px solid rgba(0, 0, 0, 0.23);
        }

        &:first-child.v-item--active {
            background-color: rgba(76, 175, 80, 0.2);
        }

        &:last-child.v-item--active {
            background-color: rgba(244, 67, 54, 0.2);
        }
    }

    .v-label {
        padding-left: 0;
    }
}

::v-deep .checklist-radio.error--text .v-input__slot {
    border-color: var(--v-error-base);
}

</style>
